import React, { useEffect } from 'react';
import { OptimizelyProvider, createInstance } from '@optimizely/react-sdk';
import { GuildUser } from '@guildeducationinc/guild-auth';
import useEmployerService from '@src/hooks/useEmployerService';
import { useProfileCompletionContext } from '@src/context/ProfileCompletionContext';

/**
 * App-level wrapper to provide access to Optimizely's APIs.
 * Passing the user ID allows us to get the experiment variation
 * based on configuration in Optimizely.
 * @packageDocumentation
 */

declare global {
  interface Window {
    optimizelyClientInstance: any;
  }
}

interface GuildOptimizelyProviderProps {
  user?: GuildUser;
}

const GuildOptimizelyProvider: React.FC<GuildOptimizelyProviderProps> = ({ children, user }) => {
  const { employer } = useEmployerService(user?.employerId);
  const { isDPPEnrolled, isTREnrolled } = useProfileCompletionContext();

  const attributes = {
    employer_uuid: user?.employerId || '',
    employer_name: employer?.name || '',
    email: user?.email || '',
    guild_user_id: user?.id || '',
    DPP_enrolled: isDPPEnrolled,
    TR_enrolled: isTREnrolled,
  };

  const optimizelyClientInstance = createInstance({
    sdkKey: process.env.REACT_APP_OPTIMIZELY_SDK_KEY,
    eventBatchSize: 10,
    eventFlushInterval: 1000,
    datafileOptions: {
      // proxy the request through Guild domains to prevent ad blocker and firewall issues
      urlTemplate: `${process.env.REACT_APP_OPTIMIZELY_BASE_URL}/optimizelydatafile=%s.json`,
    },
  });

  useEffect(() => {
    // set 'optimizelyClientInstance' on the window for Segment integration
    if (!window.optimizelyClientInstance) {
      window.optimizelyClientInstance = optimizelyClientInstance;
    }
  }, [optimizelyClientInstance]);

  return (
    <OptimizelyProvider
      optimizely={optimizelyClientInstance}
      timeout={1000}
      user={{ id: user?.id || '', attributes }}
    >
      {children}
    </OptimizelyProvider>
  );
};

export default GuildOptimizelyProvider;
